import { styled, Typography, TypographyProps } from '@mui/material'
import React from 'react'

import { cannot } from '@/app/helpers'
import { TWhasappContentAction, TWhatsappActionsContent } from '@/app/types'

import WhatsappText from './whatsapp-text'

type Props = {
  content: TWhatsappActionsContent
  variant?: TypographyProps['variant']
}

const WhatsappActions: React.FC<Props> = ({ content, variant }) => (
  <Container>
    <Message message={content.message} variant={variant} />
    <Typography variant="body2" color="textSecondary">
      Actions:
    </Typography>
    <ActionsList>
      {content.actions.map((action, index) => (
        <ActionRow data-testid={`whatsapp-actions-item-${index}`} key={index}>
          <ActionLabel variant="subtitle2" color="textSecondary">
            {getActionLabel(action)}
          </ActionLabel>
          <ActionText variant="body2" color="textPrimary">
            {getActionText(action)}
          </ActionText>
        </ActionRow>
      ))}
    </ActionsList>
  </Container>
)

const getActionLabel = (action: TWhasappContentAction): string => {
  if ('url' in action) {
    return 'URL'
  }

  if ('phone' in action) {
    return 'Phone'
  }

  if ('ingressCall' in action) {
    return 'Call Request'
  }

  if ('egressCallRequest' in action) {
    return 'Outbound Call'
  }

  if ('copyCode' in action) {
    return 'Code'
  }

  return cannot(action)
}

const getActionText = (action: TWhasappContentAction): string => {
  if ('url' in action) {
    return `${action.url.title} — ${action.url.url}`
  }

  if ('phone' in action) {
    return `${action.phone.title} — ${action.phone.phone}`
  }

  if ('ingressCall' in action) {
    return `${action.ingressCall.title} — expires in ${action.ingressCall.ttlMinutes} min`
  }

  if ('egressCallRequest' in action) {
    return `${action.egressCallRequest.title}`
  }

  if ('copyCode' in action) {
    return `${action.copyCode.title} — ${action.copyCode.code}`
  }

  return cannot(action)
}

const Container = styled('div')(({ theme }) => ({
  marginLeft: -10,
  marginRight: -10,
  width: '100%',
  boxSizing: 'border-box',
  padding: theme.spacing(0, 1.25),
}))

const Message = styled(WhatsappText)(() => ({
  marginBottom: 8,
}))

const ActionsList = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}))

const ActionRow = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
}))

const ActionLabel = styled(Typography)(() => ({
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  fontSize: 12,
}))

const ActionText = styled(Typography)(() => ({
  wordBreak: 'break-word',
}))

export default WhatsappActions
